import React, { useEffect, useRef, useState, useMemo } from 'react';
import Layouts from '../layouts/Layouts';
import { getAuthorizationHeader, http } from '../config/http';
import SpinnerGetData from '../components/common/SpinnerGetData';
import {
  IconCalendar,
  IconPrinter,
  IconTrash,
  IconBuildingStore,
  IconWallet,
  IconArrowDownRight,
  IconArrowUpRight,
  IconReceiptTax,
  IconCash,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCheck,
} from '@tabler/icons-react';
import { Calendar } from 'react-date-range';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import LayoutsKasir from '../layouts/LayoutsKasir';
import LaporanCashflowKasir from './Kasir/LaporanCashflowKasir';
import { DinamicTitle } from '../utils/DinamicTitle';
import CustomLaporanCashflow from './CustomLaporanCashflow';
import useCabang from './features/useCabang';
import { useSidebar } from '../context/SidebarContext';

export const getTipeLabel = (tipe) => {
  if (!tipe) return '';
  switch (tipe.toLowerCase()) {
    case 'pemasukan': return 'Pemasukan';
    case 'tambah-kas': return 'Tambah KAS';
    case 'tarik-kas': return 'Tarik KAS';
    case 'm-banking': return 'M-Banking';
    case 'digitalpay-transfer':
    case 'digitalpay transfer':
      return 'DigitalPay Transfer';
    case 'digitalpay-qris':
    case 'digitalpay qris':
      return 'DigitalPay QRIS';
    case 'digitalpay-edc-debit-kredit':
    case 'digitalpay edc/debit/kredit':
      return 'DigitalPay EDC/DEBIT/KREDIT';
    case 'gaji-karyawan': return 'Gaji Karyawan';
    case 'indihome': return 'Indihome';
    case 'asuransi': return 'Asuransi';
    case 'listrik': return 'Listrik';
    case 'bensin': return 'Bensin';
    case 'parkir': return 'Parkir';
    case 'makan': return 'Makan';
    case 'donasi-santunan': return 'Donasi dan Santunan';
    case 'pengeluaran-lainnya': return 'Pengeluaran Lainnya';
    default:
      return tipe
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  }
};

const LaporanCashflow = () => {
  DinamicTitle('Laporan Cashflow Harian');
  const navigate = useNavigate();
  const role = localStorage.getItem('role');
  const userRole = (role || '').toLowerCase();
  const isOperatorToko =
    userRole === 'operator' ||
    userRole === 'operator_toko' ||
    userRole === 'kepala_toko';

  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const { cabang } = useCabang();
  const [selectedCabang, setSelectedCabang] = useState(() => {
    if (isOperatorToko) {
      return localStorage.getItem('cabang_id') || '';
    }
    return '';
  });

  const handlePrint = () => {
    const isSidebarShown = Boolean(
      sidebarOpen ||
      document.querySelector('button[aria-controls="sidebar"]')?.getAttribute('aria-expanded') === 'true'
    );

    if (isSidebarShown) {
      if (typeof setSidebarOpen === 'function') {
        setSidebarOpen(false);
      }
      setTimeout(() => {
        window.print();
      }, 350);
    } else {
      window.print();
    }
  };
  const [tglFilter, setTglFilter] = useState(null);
  const [laporanHarian, setLaporanHarian] = useState({});
  const [trxMbanking, setTrxMbanking] = useState(0);
  const [pengeluaran, setPengeluaran] = useState(0);
  const [trxLuar, setTrxLuar] = useState([]);
  const [loadingLaporanHarian, setLoadingLaporanHarian] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef(null);

  // Filter cabang: toko / etalase (bukan brankas)
  const filteredCabangList = useMemo(() => {
    return (cabang || []).filter(
      (cb) => !cb.nama_cabang?.toLowerCase().includes('brankas')
    );
  }, [cabang]);

  // Auto-select operator store if user is operator toko
  useEffect(() => {
    if (isOperatorToko && filteredCabangList.length > 0) {
      const userCabangId = localStorage.getItem('cabang_id');
      const userTokoName = (localStorage.getItem('toko') || '').toLowerCase().trim();

      let targetCabang = null;
      if (userCabangId) {
        targetCabang = filteredCabangList.find((cb) => String(cb.id) === String(userCabangId));
      }
      if (!targetCabang && userTokoName) {
        targetCabang = filteredCabangList.find((cb) => {
          const cbName = (cb.nama_cabang || '').toLowerCase().trim();
          return cbName === userTokoName || cbName.includes(userTokoName) || userTokoName.includes(cbName);
        });
      }
      if (!targetCabang && filteredCabangList.length === 1) {
        targetCabang = filteredCabangList[0];
      }
      if (targetCabang && String(selectedCabang) !== String(targetCabang.id)) {
        setSelectedCabang(targetCabang.id);
      }
    }
  }, [isOperatorToko, filteredCabangList, selectedCabang]);

  const activeCabang = useMemo(() => {
    return filteredCabangList.find((cb) => String(cb.id) === String(selectedCabang));
  }, [filteredCabangList, selectedCabang]);

  // Close calendar popover on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (calendarOpen && calendarRef.current && !calendarRef.current.contains(e.target)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [calendarOpen]);

  const getPengeluaranHarian = async () => {
    // Admin / owner / operator harus pilih cabang terlebih dahulu
    if (role !== 'kasir' && !selectedCabang) {
      setLaporanHarian({});
      setTrxLuar([]);
      setTrxMbanking(0);
      setPengeluaran(0);
      return;
    }

    setLoadingLaporanHarian(true);
    const params = [];
    if (tglFilter !== null) params.push(`tanggal=${tglFilter}`);
    if (role !== 'kasir' && selectedCabang) {
      params.push(`cabang=${selectedCabang}`);
      params.push(`cabang_id=${selectedCabang}`);
    }
    const query = params.length > 0 ? `?${params.join('&')}` : '';

    await http
      .get(`laporan_harian${query}`, {
        headers: { Authorization: getAuthorizationHeader() },
      })
      .then((res) => {
        setLaporanHarian(res.data.data || {});
        setTrxLuar(res.data.data?.cashflow || []);
        setTrxMbanking(res.data.data?.summary?.total_m_banking || 0);
        setPengeluaran(res.data.data?.summary?.total_pengeluaran || 0);
      })
      .catch((err) => {
        if (!err.response) {
          return null;
        } else if (err.response.status === 403) {
          if (role === 'kasir') {
            navigate('/login');
          }
        }
      });
    setLoadingLaporanHarian(false);
  };

  useEffect(() => {
    getPengeluaranHarian();
  }, [tglFilter, selectedCabang]);

  function numberWithCommas(x) {
    if (x === null || x === undefined || isNaN(x)) return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function roundNumber(number) {
    if (number === null || number === undefined || isNaN(number)) return '0.000';
    return parseFloat(number).toFixed(3);
  }

  function handleSelect(date) {
    const tgl = moment(date).format('DD-MM-YYYY');
    setTglFilter(tgl);
    setCalendarOpen(false);
  }

  const navigateDay = (direction) => {
    const baseDate = tglFilter
      ? moment(tglFilter, 'DD-MM-YYYY')
      : laporanHarian?.tanggal
      ? moment(laporanHarian.tanggal, 'DD-MM-YYYY')
      : moment();
    const targetDate = direction === 'prev' ? baseDate.subtract(1, 'day') : baseDate.add(1, 'day');
    setTglFilter(targetDate.format('DD-MM-YYYY'));
  };

  const setToday = () => {
    setTglFilter(moment().format('DD-MM-YYYY'));
  };

  // State Barang Terkini
  const [barangTerkini, setBarangTerkini] = useState([]);
  const [totalBeratBarangTerkini, setTotalBeratBarangTerkini] = useState(0);
  const [totalHargaBarangTerkini, setTotalHargaBarangTerkini] = useState(0);
  const [totalBijiBarangTerkini, setTotalBijiBarangTerkini] = useState(0);
  const [loadingBarangTerkini, setLoadingBarangTerkini] = useState(false);

  const [customBerat, setCustomBerat] = useState('');
  const [customNama, setCustomNama] = useState('');
  const [customArray, setCustomArray] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleDelete = (indexToDelete) => {
    const newArray = customArray.filter((_, index) => index !== indexToDelete);
    setCustomArray(newArray);
  };

  const dropdown = useRef(null);
  const trigger = useRef(null);

  function submitCustom() {
    if (customBerat !== '' && customNama !== '') {
      const newItem = { berat: customBerat, nama: customNama };
      setCustomArray((prevArray) => [...prevArray, newItem]);
      setCustomBerat('');
      setCustomNama('');
    }
  }

  let customJumlah = 0;

  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdownOpen || dropdown.current?.contains(target) || trigger.current?.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [dropdownOpen]);

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [dropdownOpen]);

  async function getBarangTerkini() {
    const id_brankas = localStorage.getItem('id_brankas');
    const cabangId = role !== 'kasir' ? selectedCabang : id_brankas;

    if (role !== 'kasir' && !selectedCabang) {
      setBarangTerkini([]);
      return;
    }

    setLoadingBarangTerkini(true);
    await http
      .get(`summary/item_terkini?cabang=${cabangId}`, {
        headers: { Authorization: getAuthorizationHeader() },
      })
      .then((res) => {
        setTotalBijiBarangTerkini(res.data.total_item || 0);
        setTotalBeratBarangTerkini(res.data.total_berat || 0);
        setTotalHargaBarangTerkini(res.data.total_modal || 0);
        setBarangTerkini(res.data.data || []);
      })
      .catch((err) => {
        if (!err.response) {
          return null;
        } else if (err.response.status === 401) {
          navigate('/login');
        }
      });
    setLoadingBarangTerkini(false);
  }

  useEffect(() => {
    getBarangTerkini();
  }, [selectedCabang]);

  // Kasir View
  if (role === 'kasir') {
    return (
      <LayoutsKasir>
        <LaporanCashflowKasir
          laporanHarian={laporanHarian}
          loadingLaporanHarian={loadingLaporanHarian}
          numberWithCommas={numberWithCommas}
          roundNumber={roundNumber}
          trxLuar={trxLuar}
          tglFilter={tglFilter}
          handleSelect={handleSelect}
        />
      </LayoutsKasir>
    );
  }

  // Admin / Owner / Manager View
  return (
    <Layouts title="Laporan Cashflow">
      <div className="p-3 sm:p-5 lg:p-6 w-full space-y-4 max-w-7xl mx-auto min-h-[85vh]">

        {/* ========================================================================= */}
        {/* CARD 1: PILIH CABANG / TOKO                                               */}
        {/* ========================================================================= */}
        <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm print:hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 border border-amber-500/30">
                <IconBuildingStore size={22} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100">
                    {isOperatorToko ? 'TOKO ANDA' : 'PILIH CABANG / TOKO'}
                  </h2>
                </div>
              </div>
            </div>

            {/* If Operator Toko, display active store banner instead of dropdown */}
            {isOperatorToko ? (
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-amber-900 dark:text-amber-200 text-sm font-semibold">
                <IconBuildingStore size={18} className="text-amber-600 dark:text-amber-400" />
                <span>{activeCabang?.nama_cabang || localStorage.getItem('toko') || 'Cabang Operator'}</span>
              </div>
            ) : (
              /* Dropdown Select with Custom Arrow */
              <div className="relative w-full sm:w-72">
                <select
                  value={selectedCabang}
                  onChange={(e) => setSelectedCabang(e.target.value)}
                  className="w-full appearance-none h-11 px-3.5 pr-10 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-slate-800 dark:text-slate-100 transition-all cursor-pointer font-medium"
                >
                  <option value="">-- Pilih Cabang --</option>
                  {filteredCabangList.map((cb) => (
                    <option key={cb.id} value={cb.id}>
                      {cb.nama_cabang}
                    </option>
                  ))}
                </select>
                <IconChevronDown size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            )}
          </div>
        </div>

        {/* ========================================================================= */}
        {/* STATE 1: BELUM MEMILIH CABANG (INITIAL EMPTY WELCOME STATE)               */}
        {/* ========================================================================= */}
        {!selectedCabang ? (
          isOperatorToko ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm p-8 text-center my-4">
              <SpinnerGetData title="Memuat data toko Anda..." />
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm p-8 sm:p-12 text-center my-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-3xl bg-amber-500/10 dark:bg-amber-400/15 border border-amber-500/20 dark:border-amber-400/30 flex items-center justify-center text-amber-600 dark:text-amber-400 shadow-inner">
                <IconBuildingStore className="w-8 h-8 sm:w-10 sm:h-10" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">
                Silakan Pilih Cabang / Toko Terlebih Dahulu
              </h3>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-6">
                Pilih salah satu etalase toko di bawah ini untuk melihat ringkasan pembukuan buku kas harian, transaksi jual-beli, dan stok barang terkini.
              </p>

              {/* Quick Select Buttons for Available Branches */}
              <div className="flex flex-wrap items-center justify-center gap-2 max-w-2xl mx-auto">
                {filteredCabangList.map((cb) => (
                  <button
                    key={cb.id}
                    type="button"
                    onClick={() => setSelectedCabang(cb.id)}
                    className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 hover:bg-amber-50 dark:hover:bg-amber-950/40 hover:border-amber-400 dark:hover:border-amber-500 text-slate-700 dark:text-slate-200 hover:text-amber-700 dark:hover:text-amber-300 text-xs sm:text-sm font-semibold transition-all shadow-sm flex items-center gap-2 cursor-pointer active:scale-95 group"
                  >
                    <IconBuildingStore size={16} className="text-slate-400 group-hover:text-amber-500 transition-colors" />
                    <span>{cb.nama_cabang}</span>
                  </button>
                ))}
              </div>
            </div>
          )
        ) : (
          <>
            {/* ========================================================================= */}
            {/* STATE 2: SETELAH MEMILIH CABANG -> EXECUTIVE METRIC STAT CARDS            */}
            {/* ========================================================================= */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 print:hidden">
              {/* Card 1: Kas Awal */}
              <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider">Kas Awal</span>
                  <div className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400">
                    <IconWallet size={16} />
                  </div>
                </div>
                <div className="text-base sm:text-lg font-extrabold text-slate-800 dark:text-slate-100 truncate">
                  Rp. {numberWithCommas(laporanHarian?.kas)}
                </div>
                <span className="text-[11px] text-slate-400 mt-1">Modal Kas Masuk</span>
              </div>

              {/* Card 2: Pembelian */}
              <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider">Pembelian</span>
                  <div className="p-1.5 rounded-lg bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400">
                    <IconArrowDownRight size={16} />
                  </div>
                </div>
                <div className="text-base sm:text-lg font-extrabold text-rose-600 dark:text-rose-400 truncate">
                  Rp. {numberWithCommas(laporanHarian?.pembelian?.total_harga)}
                </div>
                <span className="text-[11px] text-slate-400 mt-1 truncate">
                  {roundNumber(laporanHarian?.pembelian?.total_berat)} gr
                </span>
              </div>

              {/* Card 3: Penjualan */}
              <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider">Penjualan</span>
                  <div className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
                    <IconArrowUpRight size={16} />
                  </div>
                </div>
                <div className="text-base sm:text-lg font-extrabold text-emerald-600 dark:text-emerald-400 truncate">
                  Rp. {numberWithCommas(laporanHarian?.penjualan?.total_harga)}
                </div>
                <span className="text-[11px] text-slate-400 mt-1 truncate">
                  {roundNumber(laporanHarian?.penjualan?.total_berat)} gr
                </span>
              </div>

              {/* Card 4: Luar Transaksi */}
              <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider">Luar Transaksi</span>
                  <div className="p-1.5 rounded-lg bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400">
                    <IconReceiptTax size={16} />
                  </div>
                </div>
                <div className="text-base sm:text-lg font-extrabold text-slate-800 dark:text-slate-100 truncate">
                  Rp. {numberWithCommas(pengeluaran + trxMbanking)}
                </div>
                <span className="text-[11px] text-slate-400 mt-1 truncate">
                  {trxLuar.length} Catatan
                </span>
              </div>

              {/* Card 5: Sisa Kas */}
              <div className="col-span-2 md:col-span-1 bg-amber-500/10 dark:bg-amber-500/20 p-4 rounded-2xl border border-amber-500/30 shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between text-amber-700 dark:text-amber-300 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider">Sisa Kas</span>
                  <div className="p-1.5 rounded-lg bg-amber-500 text-white">
                    <IconCash size={16} />
                  </div>
                </div>
                <div className="text-lg sm:text-xl font-black text-amber-700 dark:text-amber-300 truncate">
                  Rp. {numberWithCommas(laporanHarian?.sisa_kas)}
                </div>
                <span className="text-[11px] text-amber-600/80 dark:text-amber-400/80 mt-1 font-medium">Saldo Kas Akhir</span>
              </div>
            </div>

            {/* ========================================================================= */}
            {/* LAPORAN BUKU LEDGER CARD (MODERN FINANCIAL TABLE)                         */}
            {/* ========================================================================= */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm rounded-2xl overflow-hidden print:border print:border-black print:rounded-none">
              
              {/* Header Bar */}
              <div className="p-4 sm:p-5 bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-transparent dark:from-amber-950/30 dark:via-slate-900 border-b border-slate-200 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-3 print:bg-white print:border-black">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="font-extrabold uppercase text-base sm:text-lg text-slate-800 dark:text-slate-100 tracking-wide">
                      LAPORAN BUKU TANGGAL: <span className="font-medium sm:text-md">{laporanHarian?.tanggal || tglFilter || moment().format('DD/MM/YYYY')}</span>
                    </h1>
                  </div>
                </div>

                {/* Right controls: Prev / Today / Next + DatePicker + Print */}
                <div className="flex items-center gap-2 self-start md:self-auto print:hidden">
                  {/* Prev / Today / Next stepper */}
                  <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-xl p-0.5 border border-slate-200 dark:border-slate-700">
                    <button
                      type="button"
                      onClick={() => navigateDay('prev')}
                      className="p-1.5 text-slate-600 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors cursor-pointer"
                      title="Hari Sebelumnya"
                    >
                      <IconChevronLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={setToday}
                      className="px-2.5 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:text-amber-600 dark:hover:text-amber-400 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors cursor-pointer"
                    >
                      Hari Ini
                    </button>
                    <button
                      type="button"
                      onClick={() => navigateDay('next')}
                      className="p-1.5 text-slate-600 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors cursor-pointer"
                      title="Hari Berikutnya"
                    >
                      <IconChevronRight size={16} />
                    </button>
                  </div>

                  {/* Calendar Popover */}
                  <div className="relative" ref={calendarRef}>
                    <button
                      type="button"
                      onClick={() => setCalendarOpen(!calendarOpen)}
                      className="h-9 px-3 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                    >
                      <IconCalendar size={16} />
                      <span className="hidden sm:inline">Pilih Tanggal</span>
                    </button>
                    {calendarOpen && (
                      <div className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl rounded-2xl p-2 overflow-hidden">
                        <Calendar date={new Date()} onChange={handleSelect} />
                      </div>
                    )}
                  </div>

                  {/* Print Button */}
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="h-9 px-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer border border-slate-200 dark:border-slate-700"
                    title="Cetak Laporan"
                  >
                    <IconPrinter size={16} />
                    <span className="hidden sm:inline">Cetak</span>
                  </button>
                </div>
              </div>

              {/* Ledger Content */}
              {loadingLaporanHarian ? (
                <div className="p-12 text-center">
                  <SpinnerGetData />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse print:text-xs">
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800 print:divide-black">

                      {/* 1. KAS AWAL */}
                      <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="w-1/4 sm:w-1/5 py-4 px-4 sm:px-6 align-top border-r border-slate-200 dark:border-slate-800 print:border-r print:border-black font-bold">
                          <div className="flex items-center gap-2">
                            <span className="text-sm sm:text-base text-slate-900 dark:text-slate-100 font-extrabold uppercase tracking-wide">
                              KAS
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-400 font-normal block mt-0.5">
                            Modal Kas Awal
                          </span>
                        </td>
                        <td className="py-4 px-4 sm:px-6 align-top">
                          <span className="text-xl sm:text-2xl font-black text-slate-900 dark:text-slate-100">
                            Rp. {numberWithCommas(laporanHarian?.kas)}
                          </span>
                        </td>
                      </tr>

                      {/* 2. PEMBELIAN */}
                      <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="w-1/4 sm:w-1/5 py-4 px-4 sm:px-6 align-top border-r border-slate-200 dark:border-slate-800 print:border-r print:border-black font-bold">
                          <div className="flex items-center gap-2">
                            <span className="text-sm sm:text-base text-slate-900 dark:text-slate-100 font-extrabold uppercase tracking-wide">
                              PEMBELIAN
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-400 font-normal block mt-0.5">
                            Kas Keluar (Beli Emas)
                          </span>
                        </td>
                        <td className="py-4 px-4 sm:px-6 align-top space-y-3">
                          {laporanHarian?.pembelian?.total_harga === 0 ? (
                            <div className="italic text-slate-400">Tidak ada transaksi pembelian</div>
                          ) : (
                            <>
                              {/* Total Pembelian Header Pill */}
                              <div className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100 flex items-center flex-wrap gap-2">
                                <span className="font-extrabold text-rose-600 dark:text-rose-400">
                                  Rp. {numberWithCommas(laporanHarian?.pembelian?.total_harga)}
                                </span>
                                <span className="text-slate-400">/</span>
                                <span className="text-slate-700 dark:text-slate-300 font-semibold">
                                  {roundNumber(laporanHarian?.pembelian?.total_berat)} gram
                                </span>
                                <span className="text-slate-400">/</span>
                                <span className="text-slate-600 dark:text-slate-400 font-medium">
                                  RTT. {numberWithCommas(Math.round(laporanHarian?.pembelian?.rata_rata))}
                                </span>
                              </div>

                              {/* Karat Breakdown Badges */}
                              {laporanHarian?.pembelian?.items?.length > 0 && (
                                <div className="space-y-1.5 pt-1">
                                  {laporanHarian.pembelian.items.map((itp, i) => (
                                    <div
                                      key={i}
                                      className="flex items-center gap-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300 flex-wrap"
                                    >
                                      <span className="text-rose-500 font-black text-base leading-none">•</span>
                                      <span className="font-bold text-slate-800 dark:text-slate-100">
                                        {itp.karat} karat :
                                      </span>
                                      <span>{roundNumber(itp.total_berat)} gram</span>
                                      <span className="text-slate-400">/</span>
                                      <span className="font-semibold text-slate-900 dark:text-slate-100">
                                        Rp. {numberWithCommas(itp.total_harga)}
                                      </span>
                                      <span className="text-slate-400">/</span>
                                      <span className="text-slate-500 dark:text-slate-400 font-medium">
                                        RTT. {numberWithCommas(Math.round(itp.rata_rata))}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>

                      {/* 3. PENJUALAN */}
                      <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="w-1/4 sm:w-1/5 py-4 px-4 sm:px-6 align-top border-r border-slate-200 dark:border-slate-800 print:border-r print:border-black font-bold">
                          <div className="flex items-center gap-2">
                            <span className="text-sm sm:text-base text-slate-900 dark:text-slate-100 font-extrabold uppercase tracking-wide">
                              PENJUALAN
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-400 font-normal block mt-0.5">
                            Kas Masuk (Jual Emas)
                          </span>
                        </td>
                        <td className="py-4 px-4 sm:px-6 align-top space-y-3">
                          {laporanHarian?.penjualan?.total_harga === 0 ? (
                            <div className="italic text-slate-400">Tidak ada transaksi penjualan</div>
                          ) : (
                            <>
                              {/* Total Penjualan Header Pill */}
                              <div className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100 flex items-center flex-wrap gap-2">
                                <span className="font-extrabold text-emerald-600 dark:text-emerald-400">
                                  Rp. {numberWithCommas(laporanHarian?.penjualan?.total_harga)}
                                </span>
                                <span className="text-slate-400">/</span>
                                <span className="text-slate-700 dark:text-slate-300 font-semibold">
                                  {roundNumber(laporanHarian?.penjualan?.total_berat)} gram
                                </span>
                                <span className="text-slate-400">/</span>
                                <span className="text-slate-600 dark:text-slate-400 font-medium">
                                  RTT. {numberWithCommas(Math.round(laporanHarian?.penjualan?.rata_rata))}
                                </span>
                              </div>

                              {/* Karat Breakdown Badges */}
                              {laporanHarian?.penjualan?.items?.length > 0 && (
                                <div className="space-y-1.5 pt-1">
                                  {laporanHarian.penjualan.items.map((itp, i) => (
                                    <div
                                      key={i}
                                      className="flex items-center gap-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300 flex-wrap"
                                    >
                                      <span className="text-emerald-500 font-black text-base leading-none">•</span>
                                      <span className="font-bold text-slate-800 dark:text-slate-100">
                                        {itp.karat} karat :
                                      </span>
                                      <span>{itp.total_item} pcs</span>
                                      <span className="text-slate-400">/</span>
                                      <span>{roundNumber(itp.total_berat)} gr</span>
                                      <span className="text-slate-400">/</span>
                                      <span className="font-semibold text-slate-900 dark:text-slate-100">
                                        Rp. {numberWithCommas(itp.total_harga)}
                                      </span>
                                      <span className="text-slate-400">/</span>
                                      <span className="text-slate-500 dark:text-slate-400 font-medium">
                                        RTT. {numberWithCommas(Math.round(itp.rata_rata, 1))}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>

                      {/* 4. LUAR TRANSAKSI (Pengeluaran & Pemasukan Operasional / Non-Tunai) */}
                      <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="w-1/4 sm:w-1/5 py-4 px-4 sm:px-6 align-top border-r border-slate-200 dark:border-slate-800 print:border-r print:border-black font-bold">
                          <span className="text-sm sm:text-base text-slate-900 dark:text-slate-100 font-extrabold uppercase tracking-wide block">
                            LUAR TRANSAKSI
                          </span>
                          <span className="text-[11px] text-slate-400 font-medium block mt-0.5 uppercase tracking-wider">
                            PENGELUARAN PEMASUKAN
                          </span>
                        </td>
                        <td className="py-4 px-4 sm:px-6 align-top">
                          {trxLuar.length === 0 ? (
                            <div className="italic text-slate-400">Tidak ada pengeluaran / pemasukan luar transaksi</div>
                          ) : (
                            <div className="space-y-2">
                              {trxLuar.map((res, i) => {
                                const tipePengeluaran = getTipeLabel(res.tipe) || res.title || 'Pengeluaran';
                                const rawKeterangan =
                                  res.keterangan &&
                                  res.keterangan.trim() !== '' &&
                                  res.keterangan.trim() !== '-' &&
                                  res.keterangan.trim() !== '.' &&
                                  res.keterangan.trim().toLowerCase() !== tipePengeluaran.toLowerCase()
                                    ? res.keterangan.trim()
                                    : res.title &&
                                      res.title !== '-' &&
                                      res.title.trim().toLowerCase() !== tipePengeluaran.toLowerCase()
                                    ? res.title.trim()
                                    : '';

                                return (
                                  <div
                                    key={i}
                                    className="flex items-center gap-2 text-xs sm:text-sm flex-wrap text-slate-800 dark:text-slate-200 print:text-black"
                                  >
                                    <span className="font-bold text-slate-500 dark:text-slate-400 print:text-black">-</span>
                                    <span className="font-semibold text-slate-800 dark:text-slate-200 print:text-black">
                                      {tipePengeluaran}
                                    </span>
                                    <span className="text-slate-400 print:text-black">/</span>
                                    <span className="font-bold text-slate-900 dark:text-slate-100 print:text-black">
                                      Rp. {numberWithCommas(res.nominal)}
                                    </span>
                                    {rawKeterangan && (
                                      <>
                                        <span className="text-slate-400 print:text-black">-</span>
                                        <span className="text-slate-600 dark:text-slate-300 font-medium italic print:text-black">
                                          {rawKeterangan}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      </tr>

                      {/* 5. SISA KAS */}
                      <tr className="bg-gradient-to-r from-amber-500/10 via-emerald-500/5 to-transparent dark:from-amber-950/20 dark:via-emerald-950/10 print:bg-white font-black">
                        <td className="w-1/4 sm:w-1/5 py-4 sm:py-5 px-4 sm:px-6 align-top border-r border-slate-200 dark:border-slate-800 print:border-r print:border-black">
                          <span className="text-base sm:text-xl text-slate-900 dark:text-slate-100 font-black uppercase tracking-wider block">
                            SISA KAS
                          </span>
                          <span className="text-[11px] text-slate-400 font-normal block mt-0.5">
                            Saldo Akhir Hari Ini
                          </span>
                        </td>
                        <td className="py-4 sm:py-5 px-4 sm:px-6 align-top">
                          <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400">
                            Rp. {numberWithCommas(laporanHarian?.sisa_kas)}
                          </span>
                        </td>
                      </tr>

                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ========================================================================= */}
            {/* STOK BARANG TERKINI / BARANG BRANKAS / ETALASE                            */}
            {/* ========================================================================= */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm p-4 sm:p-5 overflow-hidden print:border print:border-black print:rounded-none">
              {loadingBarangTerkini ? (
                <div className="p-8 text-center">
                  <SpinnerGetData />
                </div>
              ) : (
                <>
                  <div className="pb-3 mb-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-sm sm:text-base text-slate-800 dark:text-slate-100 uppercase tracking-wide">
                        Stok Barang Terkini ({activeCabang?.nama_cabang || 'Toko'})
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Rangkuman jumlah pcs, total berat, dan modal barang etalase
                      </p>
                    </div>

                    <div className="print:hidden">
                      <CustomLaporanCashflow
                        customBerat={customBerat}
                        customNama={customNama}
                        submitCustom={submitCustom}
                        setCustomBerat={setCustomBerat}
                        dropdown={dropdown}
                        dropdownOpen={dropdownOpen}
                        setDropdownOpen={setDropdownOpen}
                        trigger={trigger}
                        setCustomNama={setCustomNama}
                      />
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden print:border-black">
                      <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 text-xs font-bold uppercase tracking-wider">
                        <tr className="border-b border-slate-200 dark:border-slate-700">
                          <th className="px-3 py-2.5 w-12 text-center">No</th>
                          <th className="px-3 py-2.5">Kategori</th>
                          <th className="px-3 py-2.5 text-center">PCS</th>
                          <th className="px-3 py-2.5 text-center">Berat (gr)</th>
                          <th className="px-3 py-2.5 text-right">Modal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200 text-sm">
                        {barangTerkini.map((bt, index) => (
                          <tr key={index} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="px-3 py-2 text-center font-medium text-slate-400">{index + 1}.</td>
                            <td className="px-3 py-2 font-semibold">{bt.kategori}</td>
                            <td className="px-3 py-2 text-center">{bt.jumlah_item_terkini}</td>
                            <td className="px-3 py-2 text-center font-medium">{bt.total_berat_terkini}</td>
                            <td className="px-3 py-2 text-right font-medium">Rp. {numberWithCommas(bt.total_modal_terkini)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50/80 dark:bg-slate-800/80 font-bold border-t-2 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 text-sm">
                        <tr>
                          <th></th>
                          <th className="px-3 py-2.5">Jumlah</th>
                          <th className="px-3 py-2.5 text-center text-rose-600 dark:text-rose-400 font-extrabold">
                            {totalBijiBarangTerkini} <span className="font-normal text-xs text-slate-500">pcs</span>
                          </th>
                          <th className="px-3 py-2.5 text-center text-rose-600 dark:text-rose-400 font-extrabold">
                            {totalBeratBarangTerkini} <span className="font-normal text-xs text-slate-500">gr</span>
                          </th>
                          <th className="px-3 py-2.5 text-right text-rose-600 dark:text-rose-400 font-extrabold">
                            Rp. {numberWithCommas(totalHargaBarangTerkini || 0)}
                          </th>
                        </tr>
                      </tfoot>
                    </table>

                    {/* Custom Array Table */}
                    {customArray.length > 0 && (
                      <div className="mt-3">
                        <table className="w-full text-sm text-left border border-dashed border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {customArray.map((ca, index) => {
                              customJumlah += parseFloat(ca.berat || 0);
                              return (
                                <tr key={index} className="text-slate-700 dark:text-slate-300 text-xs sm:text-sm">
                                  <td className="px-3 py-1.5 w-12 text-center text-slate-400">•</td>
                                  <td className="px-3 py-1.5 font-medium">{ca?.nama}</td>
                                  <td className="px-3 py-1.5 text-center">{ca?.berat} gram</td>
                                  <td className="px-3 py-1.5 text-right w-12 print:hidden">
                                    <button
                                      type="button"
                                      onClick={() => handleDelete(index)}
                                      className="p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded cursor-pointer"
                                      title="Hapus Item"
                                    >
                                      <IconTrash size={16} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-slate-50 dark:bg-slate-800/80 font-bold border-t border-slate-200 dark:border-slate-700 text-xs sm:text-sm">
                            <tr>
                              <th></th>
                              <th className="px-3 py-2">Total Gabungan (Stok + Custom):</th>
                              <th className="px-3 py-2 text-center text-rose-600 dark:text-rose-400">
                                {roundNumber(parseFloat(totalBeratBarangTerkini || 0) + customJumlah)} gram
                              </th>
                              <th className="print:hidden"></th>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Layouts>
  );
};

export default LaporanCashflow;
